import toBool from 'utils/toBool'
import { z } from 'zod'
import { RequisitionStatus } from 'utils/enums'

const TransferClinicalApprovalFields = z.object({
	medicalObservation: z.string().optional(),
	specialtyId: z
		.string()
		.optional()
		.transform((val) => (val ? parseInt(val, 10) : undefined)),
	providerId: z
		.string()
		.optional()
		.transform((val) => (val ? parseInt(val, 10) : undefined)),
	needEscort: z.boolean().optional().transform(toBool),
	notes: z.string().optional(),
})

const schema = (args) => {
	const isClinicallyRefused =
		args?.requisitionStateId === RequisitionStatus.ClinicallyRefused
	const isWaitingApproval =
		args?.requisitionStateId === RequisitionStatus.WaitingApproval

	return TransferClinicalApprovalFields.refine(
		(data) => isClinicallyRefused || data.specialtyId,
		{
			message: 'Required',
			path: ['specialtyId'],
		}
	)
		.refine(
			(data) =>
				isClinicallyRefused || isWaitingApproval || data.providerId,
			{
				message: 'Required',
				path: ['providerId'],
			}
		)
		.refine(
			(data) => !args?.requestEscort || data?.needEscort !== undefined,
			{
				message: 'Required',
				path: ['needEscort'],
			}
		)
}

const strictSchema = (args) => {
	const isClinicallyRefused =
		args?.requisitionStateId === RequisitionStatus.ClinicallyRefused
	const isWaitingApproval =
		args?.requisitionStateId === RequisitionStatus.WaitingApproval

	return TransferClinicalApprovalFields.strict()
		.refine((data) => isClinicallyRefused || data.specialtyId, {
			message: 'Required',
			path: ['specialtyId'],
		})
		.refine(
			(data) =>
				isClinicallyRefused || isWaitingApproval || data.providerId,
			{
				message: 'Required',
				path: ['providerId'],
			}
		)
		.refine(
			(data) => !args?.requestEscort || data?.needEscort !== undefined,
			{
				message: 'Required',
				path: ['needEscort'],
			}
		)
}

const managerSchema = (args) => {
	const isClinicallyRefused =
		args?.requisitionStateId === RequisitionStatus.ClinicallyRefused
	const isWaitingApproval =
		args?.requisitionStateId === RequisitionStatus.WaitingApproval

	return z
		.object({
			administrativeObservation: z.string().optional(),
			specialtyId: z
				.string()
				.optional()
				.transform((val) => (val ? parseInt(val, 10) : undefined)),
			providerId: z
				.string()
				.optional()
				.transform((val) => (val ? parseInt(val, 10) : undefined)),
			needsOptions: z
				.object({
					housing: z.boolean().optional().transform(toBool),
					transport: z.boolean().optional().transform(toBool),
				})
				.strict()
				.optional()
				.refine(
					(data) =>
						data?.housing !== undefined || !args?.requestEscort,
					{
						message: 'Required',
						path: ['housing'],
					}
				)
				.refine(
					(data) =>
						data?.transport !== undefined || !args?.requestEscort,
					{
						message: 'Required',
						path: ['transport'],
					}
				),
			notes: z.string().optional(),
			appointmentsProtocol: z
				.array(
					z.object({
						quantity: z.number().min(1),
						specialtyId: z.string(),
						serviceProviderId: z.string().optional(),
					})
				)
				.optional(),
			medicalActsProtocol: z
				.array(
					z.object({
						quantity: z.number().min(1),
						medicalActId: z.string(),
						serviceProviderId: z.string().optional(),
					})
				)
				.optional(),
		})
		.refine((data) => isClinicallyRefused || data.specialtyId, {
			message: 'Required',
			path: ['specialtyId'],
		})
		.refine(
			(data) =>
				isClinicallyRefused || isWaitingApproval || data.providerId,
			{
				message: 'Required',
				path: ['providerId'],
			}
		)
}

export const ApproveTransferClinicalSchema = (args) => schema(args)

export const TransferClinicalApprovalSchema = (args) => strictSchema(args)

export const ExtendedTransferClinicalApprovalSchema = (args) =>
	managerSchema(args)
